import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-background py-12" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Home
        </Link>

        <div className="bg-white rounded-xl border border-stone-200 p-8 md:p-12">
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">
            Terms and Conditions
          </h1>
          <p className="text-muted-foreground mb-8">
            for the Healthy Homes NZ Tradie Portal
          </p>
          <p className="text-sm text-muted-foreground mb-8">Last Updated: May 2026</p>

          <div className="prose prose-stone max-w-none">
            <p className="text-foreground mb-6">
              Welcome to the Healthy Homes NZ Tradie Portal (accessible at tradies.healthyhomesnz.co.nz). 
              This portal is owned and operated by Healthy Homes NZ Limited ("we", "us", "our").
            </p>
            <p className="text-foreground mb-8">
              By accessing, registering an account, or using our portal—whether as a tradesperson or service 
              provider ("Trade Partner") or as a landlord, property manager, or homeowner ("Client")—you agree 
              to be bound by these Terms and Conditions.
            </p>

            {/* Section 1 */}
            <h2 className="font-heading text-xl font-semibold mt-8 mb-4 text-primary">
              1. General Terms (Applicable to Both Trade Partners & Clients)
            </h2>

            <h3 className="font-heading text-lg font-medium mt-6 mb-2">1.1 Referral Directory Only</h3>
            <p className="text-foreground mb-4">
              The Healthy Homes NZ Tradie Portal is a referral directory. We connect Clients seeking remedial 
              property maintenance with independent Trade Partners. We do not act as a project manager, general 
              contractor, or agent for either party.
            </p>

            <h3 className="font-heading text-lg font-medium mt-6 mb-2">1.2 Independent Contracting</h3>
            <p className="text-foreground mb-4">
              Any agreement or contract for work, maintenance, or repairs is strictly between the Client and 
              the Trade Partner. Healthy Homes NZ Limited is not a party to any such agreement.
            </p>

            <h3 className="font-heading text-lg font-medium mt-6 mb-2">1.3 Complete Dispute Exclusion</h3>
            <p className="text-foreground mb-4">
              If any dispute arises regarding the quality of work, pricing, timelines, damages, or any other 
              matter, it is strictly between the Trade Partner and the Client. Healthy Homes NZ Limited will 
              not intervene, mediate, arbitrate, or accept any liability for disputes between the parties.
            </p>

            <h3 className="font-heading text-lg font-medium mt-6 mb-2">1.4 Limitation of Liability</h3>
            <p className="text-foreground mb-4">
              To the maximum extent permitted by New Zealand law, Healthy Homes NZ Limited is not liable for 
              any direct, indirect, incidental, or consequential damages resulting from interactions, contracts, 
              or work arranged through the portal.
            </p>

            {/* Section 2 */}
            <h2 className="font-heading text-xl font-semibold mt-10 mb-4 text-primary">
              2. Trade Partner Terms & Conditions
            </h2>

            <h3 className="font-heading text-lg font-medium mt-6 mb-2">
              2.1 Subscription Fees, Stripe Payments & Launch Promotion
            </h3>

            <h4 className="font-medium mt-4 mb-2">2.1.1 Early Bird Promotion</h4>
            <p className="text-foreground mb-4">
              Trade Partners who sign up on or before the official launch date of 1 June 2026 will receive 
              a 3-month free trial of the Tradie Portal services.
            </p>

            <h4 className="font-medium mt-4 mb-2">2.1.2 Ongoing Subscription</h4>
            <p className="text-foreground mb-4">
              Upon the expiration of the 3-month trial, the subscription automatically converts to a paid 
              model of $49 NZD (plus GST, if applicable) per month, unless cancelled in writing.
            </p>

            <h4 className="font-medium mt-4 mb-2">2.1.3 No Success Fees</h4>
            <p className="text-foreground mb-4">
              We do not charge commissions, success fees, or scaled referral costs on any work you secure 
              through the portal.
            </p>

            <h4 className="font-medium mt-4 mb-2">2.1.4 Stripe Payments</h4>
            <p className="text-foreground mb-4">
              All subscription fees are processed securely via our third-party payment provider, Stripe. 
              By signing up for a paid subscription, you agree to Stripe's Terms of Service and authorise 
              us to automatically charge your nominated credit or debit card each month.
            </p>

            <h4 className="font-medium mt-4 mb-2">2.1.5 Cancellation</h4>
            <p className="text-foreground mb-4">
              You may cancel your subscription at any time. To avoid being billed for the subsequent month, 
              you must provide written notice at least 7 days before your next billing date. Subscription 
              fees are non-refundable.
            </p>

            <h3 className="font-heading text-lg font-medium mt-6 mb-2">
              2.2 Trade Partner Obligations & Warranties
            </h3>
            <p className="text-foreground mb-4">
              By registering on the portal, you warrant and agree that:
            </p>

            <h4 className="font-medium mt-4 mb-2">2.2.1 Credentials & Licensing</h4>
            <p className="text-foreground mb-4">
              You hold all current, valid New Zealand trade qualifications, licences, and certifications 
              required by law to carry out your advertised services.
            </p>

            <h4 className="font-medium mt-4 mb-2">2.2.2 Work Standards</h4>
            <p className="text-foreground mb-4">
              All work you perform for Clients will meet the relevant New Zealand building codes, safety 
              standards, and Healthy Homes Standards.
            </p>

            <h4 className="font-medium mt-4 mb-2">2.2.3 Insurance</h4>
            <p className="text-foreground mb-4">
              You maintain active Public Liability Insurance of at least $2,000,000 NZD to cover any 
              potential damages or liabilities arising from the work you undertake.
            </p>

            <h4 className="font-medium mt-4 mb-2">2.2.4 Direct Invoicing</h4>
            <p className="text-foreground mb-4">
              You are solely responsible for quoting, invoicing, and collecting payments directly from 
              the Client for any work completed.
            </p>

            {/* Section 3 */}
            <h2 className="font-heading text-xl font-semibold mt-10 mb-4 text-primary">
              3. Client Terms & Conditions
            </h2>

            <h3 className="font-heading text-lg font-medium mt-6 mb-2">3.1 Free Directory Use</h3>
            <p className="text-foreground mb-4">
              Sourcing, searching, and contacting Trade Partners through the Healthy Homes NZ Tradie Portal 
              is free of charge for Clients.
            </p>

            <h3 className="font-heading text-lg font-medium mt-6 mb-2">3.2 Verification of Credentials</h3>
            <p className="text-foreground mb-4">
              While we require Trade Partners to confirm they hold valid qualifications and insurance upon 
              registration, the Client is solely responsible for verifying these credentials, viewing 
              certificates, and checking references before engaging a Trade Partner.
            </p>

            <h3 className="font-heading text-lg font-medium mt-6 mb-2">3.3 No Guarantee of Workmanship</h3>
            <p className="text-foreground mb-4">
              Healthy Homes NZ Limited does not warrant, verify, or guarantee the quality, reliability, 
              safety, or legality of the work performed by any Trade Partner found on the portal.
            </p>

            <h3 className="font-heading text-lg font-medium mt-6 mb-2">3.4 Direct Payments</h3>
            <p className="text-foreground mb-4">
              The Client is responsible for paying the Trade Partner directly according to the specific 
              payment terms agreed upon between the Client and the Trade Partner.
            </p>

            {/* Section 4 */}
            <h2 className="font-heading text-xl font-semibold mt-10 mb-4 text-primary">
              4. Account Suspension & Portal Availability
            </h2>

            <h3 className="font-heading text-lg font-medium mt-6 mb-2">4.1 Account Removal</h3>
            <p className="text-foreground mb-4">
              We reserve the right to suspend or remove any Trade Partner or Client account from the portal 
              at our sole discretion, without refund, if these terms are violated, if payment fails via 
              Stripe, or if we receive substantiated complaints regarding unprofessional conduct.
            </p>

            <h3 className="font-heading text-lg font-medium mt-6 mb-2">4.2 Portal Maintenance</h3>
            <p className="text-foreground mb-4">
              We do not guarantee uninterrupted access to the portal and are not liable for any temporary 
              downtime due to maintenance or technical difficulties.
            </p>

            {/* Section 5 */}
            <h2 className="font-heading text-xl font-semibold mt-10 mb-4 text-primary">
              5. Governing Law
            </h2>
            <p className="text-foreground mb-4">
              These Terms and Conditions are governed by the laws of New Zealand. Any disputes arising out 
              of or in connection with these terms shall be subject to the exclusive jurisdiction of the 
              New Zealand courts.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
